/**
 * P-19 系统健康（docs/03-UIUX.md §5）。
 *
 * 展示 API / DB / Signer / Queue 的健康状态与备份状态。
 * queue / backup 当前架构未部署，后端如实上报 not_configured——健康页不假装「一切正常」。
 */
import { useCallback } from 'react';
import { Icon } from '../icons/Icon';
import { ErrorBlock } from '../components/DataState';
import { describeError } from '../api/client';
import { getReadiness } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { ICON_SIZE } from '../tokens';

type ComponentStatus = 'up' | 'down' | 'not_configured';

const HEALTH_STATUS: Record<ComponentStatus, { label: string; icon: 'circle-check' | 'octagon-x' | 'circle-alert'; color: string }> = {
  up: { label: '正常', icon: 'circle-check', color: 'var(--st-live)' },
  down: { label: '异常', icon: 'octagon-x', color: 'var(--st-danger)' },
  not_configured: { label: '未配置', icon: 'circle-alert', color: 'var(--muted)' },
};

export function SystemPage() {
  const readiness = useAsync(useCallback(() => getReadiness(), []), []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">健康与系统</h1>
          <p className="page-head__sub">
            服务组件健康状态。队列与备份当前未部署，如实显示「未配置」。
          </p>
        </div>
      </div>

      {readiness.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(readiness.state.error, '获取健康状态')} onRetry={readiness.reload} />
        </div>
      ) : readiness.state.kind === 'loading' ? (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="skeleton-bar" style={{ height: 80 }} />
          <div className="skeleton-bar" style={{ height: 80 }} />
          <div className="skeleton-bar" style={{ height: 80 }} />
          <div className="skeleton-bar" style={{ height: 80 }} />
        </div>
      ) : (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <HealthCard title="API 服务" status={readiness.state.data.api} description="HTTP 接口层存活（能响应本请求即 up）" />
          <HealthCard title="数据库" status={readiness.state.data.database} description="PostgreSQL 18.4，连接池正常" />
          <HealthCard title="签名服务" status={readiness.state.data.signer} description="Ed25519 签名密钥就绪" />
          <HealthCard title="任务队列" status={readiness.state.data.queue} description="当前架构未部署消息队列" />
          <HealthCard title="备份流水线" status={readiness.state.data.backup} description="未配置自动备份；恢复依赖数据库层快照" />
        </div>
      )}
    </>
  );
}

function HealthCard({ title, status, description }: { title: string; status: ComponentStatus; description: string }) {
  const info = HEALTH_STATUS[status] ?? HEALTH_STATUS.not_configured;
  return (
    <div className="health-card">
      <div className="health-card__head">
        <Icon name={info.icon} size={ICON_SIZE.control} color={info.color} />
        <span className="health-card__title">{title}</span>
        <span className="health-card__status" style={{ color: info.color }}>{info.label}</span>
      </div>
      <p className="health-card__desc">{description}</p>
    </div>
  );
}
